import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Divider,
    Icon,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import { SimpleOrderContext } from 'pages/order-flow/simple-order/simple-order-context';
import CreateConstructionAi from './create-construction-ai/create-construction-ai.component';
import Constructions from './constructions/constructions.component';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OrderFiles from 'components/order-flow/shared/order-files/order-files.component';
import { File as DenthubFile } from 'models/file';

type ConstructionsStepProps = {
    onBackClick: () => void;
    onNextClick: () => void;
};
const ConstructionsStep: FC<ConstructionsStepProps> = ({ onBackClick, onNextClick }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { order, setOrder } = useContext(SimpleOrderContext);
    const [comment, setComment] = useState('');

    const handleCommentBlur = () => {
        setOrder((prev) => ({ ...prev, comment }));
    };

    useEffect(() => {
        setComment(order.comment);
    }, [order.comment]);

    const handleFilesChange = (files: DenthubFile[]) => {
        setOrder((prev) => ({ ...prev, files }));
    };

    return (
        <Fragment>
            <Box>
                <Box mb={2}>
                    <Typography variant="h1">{translate('orders:CONSTRUCTIONS_FILES')}</Typography>
                </Box>
                <Box mb={2}>
                    <Divider />
                </Box>
                <Box mb={4}>
                    <Accordion data-test="constructions-step.ai-accordion">
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            data-test="constructions-step.ai-accordion-summary"
                        >
                            <Box display="flex" alignItems="center">
                                <Icon sx={{ marginRight: 1 }}>
                                    <AutoAwesomeIcon sx={{ color: 'ai.main' }} />
                                </Icon>
                                <Typography variant="h4" component="span">
                                    {translate('orders:ADD_CONSTRUCTION_WITH_AI')}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails data-test="constructions-step.ai-accordion-details">
                            <CreateConstructionAi />
                        </AccordionDetails>
                    </Accordion>
                </Box>
                <Box mb={4}>
                    <Paper variant="outlined">
                        <Box p={4}>
                            <Constructions />
                        </Box>
                    </Paper>
                </Box>
                <Box mb={4}>
                    <Box mb={2}>
                        <Typography variant="h3" gutterBottom>
                            {translate('orders:COMMENT')}
                        </Typography>
                    </Box>
                    <Box>
                        <TextField
                            minRows={6}
                            multiline
                            value={comment}
                            onChange={(event) => setComment(event.target.value)}
                            onBlur={handleCommentBlur}
                            placeholder={translate('orders:COMMENT')}
                            fullWidth
                            data-test="constructions-step.comment-input"
                        />
                    </Box>
                </Box>

                <Box mb={4}>
                    <Box mb={1}>
                        <Typography variant="h3">{translate('common:FILES')}</Typography>
                    </Box>
                    <OrderFiles order={order} onFilesChanged={handleFilesChange} />
                </Box>

                <Box display="flex" mt={2}>
                    <Box flexGrow={1} textAlign="left">
                        <Button type="button" onClick={onBackClick} data-test="constructions-step.back">
                            {translate('common:BACK')}
                        </Button>
                    </Box>
                    <Box>
                        <Button
                            disabled={order.restorations.length === 0}
                            type="button"
                            onClick={onNextClick}
                            variant="contained"
                            data-test="constructions-step.next"
                        >
                            {translate('common:NEXT_STEP')}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Fragment>
    );
};

export default ConstructionsStep;
